import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import monitorApi from "../../api/monitorApi";
import closeIcon from "../../assets/img/icons/closeIcon.svg";
import crawlIcon from "../../assets/img/icons/crawl.svg";
import Header from "../../components/Header/Header";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import {
  DnsCheckDetailType,
  PrecalcResponseType,
  PrecalcRowType,
} from "./WhiteListCheckerPageTypes";

const STORAGE_KEY = "white_list_checker_sliders";
export const UPDATE_PENDING_KEY = "white_list_checker_update_pending";
export const UPDATE_POLL_LIMITS = { ms: 1000, max: 180 };

function formatSnapshotAt(iso?: string | null) {
  if (!iso) {
    return "";
  }
  return iso.replace("T", " ").slice(0, 19);
}

function isSnapshotStale(precalc: PrecalcResponseType | null) {
  if (!precalc?.latest_result_at) {
    return false;
  }
  if (!precalc.updated_at) {
    return true;
  }
  return (
    new Date(precalc.latest_result_at).getTime() >
    new Date(precalc.updated_at).getTime()
  );
}

function applyPrecalcResponse(
  payload: unknown,
): payload is PrecalcResponseType {
  return (
    typeof payload === "object" &&
    payload !== null &&
    (payload as PrecalcResponseType).status === "ok" &&
    typeof (payload as PrecalcResponseType).data === "object" &&
    (payload as PrecalcResponseType).data !== null
  );
}

type UpdatePending = { updatedAt: string | null };

function readUpdatePending(): UpdatePending | null {
  try {
    const raw = sessionStorage.getItem(UPDATE_PENDING_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { updatedAt?: string | null };
    return { updatedAt: parsed.updatedAt ?? null };
  } catch {
    return null;
  }
}

function writeUpdatePending(updatedAt: string | null) {
  sessionStorage.setItem(UPDATE_PENDING_KEY, JSON.stringify({ updatedAt }));
}

function clearUpdatePending() {
  sessionStorage.removeItem(UPDATE_PENDING_KEY);
}

function snapshotIsNewer(
  payload: PrecalcResponseType,
  previousUpdatedAt: string | null,
) {
  return (
    Boolean(payload.updated_at) && payload.updated_at !== previousUpdatedAt
  );
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function isBlobResponse(value: unknown): value is Blob {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Blob).text === "function"
  );
}

function loadSliders(): {
  whiteListThreshold: number;
  otherResourcesThreshold: number;
} {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        whiteListThreshold?: number;
        otherResourcesThreshold?: number;
      };
      const whiteListThreshold = Number(parsed.whiteListThreshold);
      const otherResourcesThreshold = Number(parsed.otherResourcesThreshold);
      if (
        !Number.isNaN(whiteListThreshold) &&
        whiteListThreshold >= 0 &&
        whiteListThreshold <= 100 &&
        !Number.isNaN(otherResourcesThreshold) &&
        otherResourcesThreshold >= 0 &&
        otherResourcesThreshold <= 100
      ) {
        return { whiteListThreshold, otherResourcesThreshold };
      }
    }
  } catch {
    // ignore
  }
  return { whiteListThreshold: 90, otherResourcesThreshold: 10 };
}

function WhiteListCheckerPage() {
  const [precalcData, setPrecalcData] = useState<PrecalcResponseType | null>(
    null,
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalCell, setModalCell] = useState<{
    row: PrecalcRowType;
    date: string;
  } | null>(null);
  const [detailData, setDetailData] = useState<DnsCheckDetailType | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRequested, setDetailRequested] = useState(false);
  const [whiteListThreshold, setWhiteListThreshold] = useState(
    () => loadSliders().whiteListThreshold,
  );
  const [otherResourcesThreshold, setOtherResourcesThreshold] = useState(
    () => loadSliders().otherResourcesThreshold,
  );
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [csvDownloadBusy, setCsvDownloadBusy] = useState(false);
  const [csvDownloadMessage, setCsvDownloadMessage] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateFailed, setUpdateFailed] = useState(false);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollCountRef = useRef(0);
  const pendingUpdatedAtRef = useRef<string | null>(null);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ whiteListThreshold, otherResourcesThreshold }),
    );
  }, [whiteListThreshold, otherResourcesThreshold]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const finishSuccess = useCallback(
    (payload: PrecalcResponseType) => {
      stopPolling();
      clearUpdatePending();
      setPrecalcData(payload);
      setIsUpdating(false);
      setUpdateFailed(false);
      setUpdateMessage("Данные обновлены");
    },
    [stopPolling],
  );

  const finishFailure = useCallback(
    (message: string) => {
      stopPolling();
      clearUpdatePending();
      setIsUpdating(false);
      setUpdateFailed(true);
      setUpdateMessage(message);
    },
    [stopPolling],
  );

  const startPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      return;
    }
    const tick = () => {
      pollCountRef.current += 1;
      if (pollCountRef.current > UPDATE_POLL_LIMITS.max) {
        stopPolling();
        setIsUpdating(false);
        setUpdateFailed(false);
        setUpdateMessage(
          "Счёт ещё идёт. Можно обновить страницу — результат подтянется.",
        );
        return;
      }
      monitorApi
        .getWhiteListPrecalc()
        .then((res) => {
          if (!applyPrecalcResponse(res.data)) {
            return;
          }
          setPrecalcData(res.data);
          if (res.data.computing) {
            return;
          }
          if (res.data.compute_error) {
            finishFailure("Не удалось обновить данные");
            return;
          }
          finishSuccess(res.data);
        })
        .catch(() => {});
    };
    tick();
    pollTimerRef.current = window.setInterval(tick, UPDATE_POLL_LIMITS.ms);
  }, [finishFailure, finishSuccess, stopPolling]);

  const beginUpdate = useCallback(
    (previousUpdatedAt: string | null) => {
      pendingUpdatedAtRef.current = previousUpdatedAt;
      writeUpdatePending(previousUpdatedAt);
      setIsUpdating(true);
      setUpdateMessage("");
      setUpdateFailed(false);
      monitorApi
        .updateWhiteListPrecalc()
        .then((res) => {
          if (!applyPrecalcResponse(res.data)) {
            throw new Error("bad status");
          }
          setPrecalcData(res.data);
          if (res.data.computing) {
            startPolling();
            return;
          }
          finishSuccess(res.data);
        })
        .catch(() => {
          return monitorApi.getWhiteListPrecalc().then((res) => {
            if (!applyPrecalcResponse(res.data)) {
              throw new Error("bad status");
            }
            setPrecalcData(res.data);
            if (res.data.computing) {
              startPolling();
              return;
            }
            if (snapshotIsNewer(res.data, pendingUpdatedAtRef.current)) {
              finishSuccess(res.data);
              return;
            }
            finishFailure("Не удалось обновить данные");
          });
        })
        .catch(() => {
          setIsUpdating(false);
          setUpdateFailed(true);
          setUpdateMessage("Не удалось обновить данные");
        });
    },
    [finishFailure, finishSuccess, startPolling],
  );

  useEffect(() => {
    const pending = readUpdatePending();
    if (pending) {
      pendingUpdatedAtRef.current = pending.updatedAt;
      setIsUpdating(true);
    }
    monitorApi
      .getWhiteListPrecalc()
      .then((res) => {
        if (!applyPrecalcResponse(res.data)) {
          return;
        }
        setPrecalcData(res.data);
        if (pending && snapshotIsNewer(res.data, pending.updatedAt)) {
          finishSuccess(res.data);
          return;
        }
        if (res.data.computing) {
          setIsUpdating(true);
          startPolling();
        }
        if (pending && !snapshotIsNewer(res.data, pending.updatedAt)) {
          beginUpdate(pending.updatedAt);
        }
      })
      .catch(() => {});
    return () => {
      stopPolling();
    };
  }, [beginUpdate, finishSuccess, startPolling, stopPolling]);

  const handleDownloadAllCsv = () => {
    setCsvDownloadBusy(true);
    setCsvDownloadMessage("");
    monitorApi
      .downloadWhiteListCsvArchive()
      .then((res) => {
        triggerBlobDownload(res.data, "white_list_checker_csv.zip");
        setCsvDownloadMessage("Архив скачан");
      })
      .catch(async (error) => {
        let errorText = "Не удалось скачать архив";
        const responseData = error.response?.data;
        if (isBlobResponse(responseData)) {
          try {
            const parsed = JSON.parse(await responseData.text());
            if (parsed?.message) {
              errorText =
                parsed.message === "CSV archive is not ready yet."
                  ? "CSV-архив ещё не собран. Подождите фоновую выгрузку."
                  : parsed.message;
            }
          } catch {
            /* keep default */
          }
        } else if (responseData?.message) {
          errorText = responseData.message;
        }
        setCsvDownloadMessage(errorText);
      })
      .finally(() => setCsvDownloadBusy(false));
  };

  const handleUpdate = () => {
    beginUpdate(precalcData?.updated_at ?? null);
  };

  const { dates, regionToDateData } = useMemo(() => {
    if (!precalcData?.data) {
      return {
        dates: [] as string[],
        regionToDateData: new Map<string, Map<string, PrecalcRowType>>(),
      };
    }
    const dates = Object.keys(precalcData.data).sort();
    const regionToDateData = new Map<string, Map<string, PrecalcRowType>>();
    for (const date of dates) {
      const rows = precalcData.data[date] ?? [];
      for (const row of rows) {
        let byDate = regionToDateData.get(row.region);
        if (!byDate) {
          byDate = new Map();
          regionToDateData.set(row.region, byDate);
        }
        byDate.set(date, row);
      }
    }
    return { dates, regionToDateData };
  }, [precalcData]);

  useEffect(() => {
    const el = tableWrapRef.current;
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }, [dates]);

  const regions = precalcData?.regions ?? [];
  const snapshotLabel = formatSnapshotAt(precalcData?.updated_at);
  const snapshotStale = isSnapshotStale(precalcData);

  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />

      <div
        data-testid="white-list-checker-page"
        className="p-[1rem] md:p-[2rem] flex flex-col w-auto max-w-[90rem] mx-auto"
      >
        <div className="w-full flex flex-row flex-wrap justify-center items-center gap-2 md:gap-4">
          <div className="flex flex-col lg:flex-row flex-nowrap items-stretch lg:items-center gap-[2rem] md:gap-[3rem] min-w-0 max-lg:flex-1 lg:flex-initial shrink">
            <label className="flex items-center gap-3">
              <span className="text-sm whitespace-nowrap">Порог БС:</span>
              <input
                type="range"
                min={0}
                max={100}
                value={whiteListThreshold}
                onChange={(e) => setWhiteListThreshold(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-sm tabular-nums w-7">
                {">"}
                {whiteListThreshold}%
              </span>
            </label>
            <label className="flex items-center gap-3">
              <span className="text-sm whitespace-nowrap">
                Порог Контрольных:
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={otherResourcesThreshold}
                onChange={(e) =>
                  setOtherResourcesThreshold(Number(e.target.value))
                }
                className="w-24"
              />
              <span className="text-sm tabular-nums w-7">
                {"<"}
                {otherResourcesThreshold}%
              </span>
            </label>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              id="white-list-checker-download-csv-button"
              data-testid="white-list-checker-download-csv-button"
              type="button"
              onClick={handleDownloadAllCsv}
              disabled={csvDownloadBusy}
              title="Скачать все CSV"
              className="shrink-0 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {csvDownloadBusy ? "Собирается…" : "Скачать все CSV"}
            </button>
            <button
              id="update-data-button"
              data-testid="update-data-button"
              type="button"
              onClick={handleUpdate}
              disabled={isUpdating}
              title="Обновить данные"
              aria-label="Обновить данные"
              className={`shrink-0 flex items-center gap-2 rounded-sm border px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                snapshotStale
                  ? "border-amber-400 bg-amber-50 text-amber-950 hover:bg-amber-100"
                  : "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <img
                src={crawlIcon}
                alt=""
                width={20}
                height={20}
                className={isUpdating ? "animate-spin" : ""}
              />
              {isUpdating ? "Считается…" : "Обновить данные"}
            </button>
            <button
              id="whitelist-checker-help-button"
              type="button"
              onClick={() => setHelpModalOpen(true)}
              title="Справка"
              aria-label="Справка по странице"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-slate-300 bg-slate-50 text-lg font-semibold text-slate-700 hover:bg-slate-200"
            >
              ?
            </button>
          </div>
        </div>
        {csvDownloadMessage ? (
          <p
            data-testid="white-list-checker-download-csv-status"
            className="mt-2 text-center text-sm text-slate-600"
          >
            {csvDownloadMessage}
          </p>
        ) : null}
        {updateMessage ? (
          <p
            data-testid="white-list-checker-update-status"
            className={`mt-2 text-center text-sm ${
              updateFailed ? "text-red-700" : "text-green-700"
            }`}
          >
            {updateMessage}
          </p>
        ) : null}
        {snapshotStale && !isUpdating ? (
          <p
            data-testid="white-list-checker-stale-hint"
            className="mt-2 text-center text-sm text-amber-800"
          >
            Есть новые результаты волонтёров. Нажмите «Обновить данные».
          </p>
        ) : null}
        {dates.length > 0 ? (
          <p
            data-testid="white-list-checker-date-range"
            className="mt-2 text-center text-sm text-slate-600"
          >
            Даты: {dates[0]} — {dates[dates.length - 1]} ({dates.length})
            {snapshotLabel ? ` · снимок ${snapshotLabel}` : ""}
          </p>
        ) : null}

        {regions.length > 0 && (
          <div ref={tableWrapRef} className="w-full overflow-x-auto mt-3">
            <table className="w-full border-collapse border border-slate-300 text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 px-3 py-2 text-left sticky left-0 bg-slate-100 z-10">
                    Регион
                  </th>
                  {dates.map((d) => (
                    <th
                      key={d}
                      className="border border-slate-300 px-3 py-2 text-center whitespace-nowrap"
                    >
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {regions.map((region) => (
                  <tr key={region} className="group hover:bg-slate-50">
                    <td className="border border-slate-300 px-3 py-2 font-medium sticky left-0 bg-white group-hover:bg-slate-50 z-10">
                      {region}
                    </td>
                    {dates.map((date) => {
                      const row = regionToDateData.get(region)?.get(date);
                      const pct = row?.result?.accessible_percentage;
                      const cell =
                        pct != null
                          ? `${pct.white_list}% / ${pct.other_resources}%`
                          : "";
                      const isEmpty = pct == null;
                      const meetsOrExceedsThreshold =
                        !isEmpty &&
                        pct.white_list >= whiteListThreshold &&
                        pct.other_resources >= otherResourcesThreshold;
                      const bgClass = isEmpty
                        ? "bg-slate-200"
                        : meetsOrExceedsThreshold
                          ? "bg-red-200"
                          : "bg-green-200";
                      return (
                        <td
                          key={date}
                          role={isEmpty ? undefined : "button"}
                          tabIndex={isEmpty ? undefined : 0}
                          onClick={
                            isEmpty
                              ? undefined
                              : () => setModalCell({ row: row!, date })
                          }
                          onKeyDown={
                            isEmpty
                              ? undefined
                              : (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setModalCell({ row: row!, date });
                                  }
                                }
                          }
                          className={`border border-slate-300 px-3 py-2 text-center tabular-nums ${bgClass} ${
                            !isEmpty ? "cursor-pointer hover:opacity-90" : ""
                          }`}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {helpModalOpen && (
        <div
          role="dialog"
          aria-label="Справка"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setHelpModalOpen(false)}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-md overflow-auto rounded-lg border border-slate-300 bg-white p-6 shadow-lg pt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Закрыть"
              className="absolute right-3 top-3 p-1 hover:opacity-70"
              onClick={() => setHelpModalOpen(false)}
            >
              <img src={closeIcon} alt="" className="h-5 w-5" />
            </button>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                На пересечении региона и даты отображаются результаты
                сканирования. В каждой ячейке могут быть обьединены несколько
                результатов проверки. Красная ячейка — обнаружен белый список в
                соответствии с порогами. Зелёная ячейка — не попадает под
                условия пороговых значений. В каждой ячейке указан процент
                доступности ресурсов по белым спискам, и по другим контрольным
                ресурсам (79% / 31%)
              </p>
              <p>
                Чтобы ячейка подсветилась красным (БС), нужно чтобы ячейка
                попала под оба условия пороговых значений: для белого списка
                значение должно быть выше «Порог БС», а для контрольных ресурсов
                не выше «Порог Контрольных».
              </p>
              <p>
                Кнопка «Обновить данные» заново считает проценты по всем
                присланным от волонтёров результатам и сохраняет снимок на
                сервере. Можно сразу обновить страницу — подсчёт не прервётся,
                результат подтянется. Если есть новые загрузки, страница
                подскажет нажать эту кнопку.
              </p>
              <p>
                «Скачать все CSV» собирает уже готовые CSV в один zip. Файлы
                заранее восстанавливаются фоновой командой; если архив ещё не
                готов, подождите и повторите.
              </p>
            </div>
          </div>
        </div>
      )}

      {modalCell && (
        <div
          role="dialog"
          aria-label="Детали ячейки"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setModalCell(null)}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-md overflow-auto rounded-lg border border-slate-300 bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Закрыть"
              className="absolute right-3 top-3 p-1 hover:opacity-70"
              onClick={() => setModalCell(null)}
            >
              <img src={closeIcon} alt="" className="h-5 w-5" />
            </button>
            <h3 className="mb-4 pr-8 text-lg font-semibold">
              {modalCell.row.region} — {modalCell.date}
            </h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-slate-500">Белые списки</dt>
                <dd className="font-medium tabular-nums">
                  {modalCell.row.result.accessible_percentage.white_list}%
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Другие ресурсы</dt>
                <dd className="font-medium tabular-nums">
                  {modalCell.row.result.accessible_percentage.other_resources}%
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">ID результатов проверки</dt>
                <dd className="font-mono text-xs flex flex-wrap gap-2">
                  {modalCell.row.result.dnscheckscriptresult_ids.length > 0
                    ? modalCell.row.result.dnscheckscriptresult_ids.map(
                        (id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              setDetailRequested(true);
                              setDetailLoading(true);
                              setDetailData(null);
                              monitorApi
                                .getDnsCheckScriptResult(id)
                                .then((res) => {
                                  if (
                                    res.data?.status === "ok" &&
                                    res.data?.data
                                  ) {
                                    setDetailData(
                                      res.data.data as DnsCheckDetailType,
                                    );
                                  }
                                })
                                .catch(() => setDetailData(null))
                                .finally(() => setDetailLoading(false));
                            }}
                            className="px-2 py-0.5 rounded border border-slate-300 bg-slate-50 hover:bg-slate-200 text-xs"
                          >
                            {id}
                          </button>
                        ),
                      )
                    : "—"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {(detailLoading || detailData || detailRequested) && (
        <div
          role="dialog"
          aria-label="Детали проверки DNS"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => {
            setDetailRequested(false);
            setDetailData(null);
            setDetailLoading(false);
          }}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-[95vw] overflow-auto rounded-lg border border-slate-300 bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-20 h-0">
              <div className="flex justify-end">
                <button
                  type="button"
                  aria-label="Закрыть"
                  className="-mt-6 -mr-6 p-1 shadow-sm hover:opacity-70"
                  onClick={() => {
                    setDetailRequested(false);
                    setDetailData(null);
                    setDetailLoading(false);
                  }}
                >
                  <img src={closeIcon} alt="" className="h-5 w-5" />
                </button>
              </div>
            </div>
            {detailLoading ? (
              <p className="py-8 text-center text-slate-500">Загрузка…</p>
            ) : detailData ? (
              <>
                <h3 className="mb-4 pr-8 text-base font-semibold sm:text-lg">
                  Результат проверки #{detailData.id}
                </h3>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-slate-500">Регион</dt>
                    <dd className="font-medium">{detailData.region}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Провайдер</dt>
                    <dd className="font-medium">{detailData.provider}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">IP отправителя</dt>
                    <dd className="font-mono text-xs">
                      {detailData.sender_ip}
                    </dd>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <dt className="text-slate-500">Доступно</dt>
                      <dd className="font-medium tabular-nums">
                        {detailData.accessible}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Частично</dt>
                      <dd className="font-medium tabular-nums">
                        {detailData.partial}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">
                        Заблокировано / недоступно
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {detailData.blocked_down}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Ошибки</dt>
                      <dd className="font-medium tabular-nums">
                        {detailData.errors}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Всего</dt>
                      <dd className="font-medium tabular-nums">
                        {detailData.total}
                      </dd>
                    </div>
                  </div>
                  {(detailData.admin_note || detailData.script_version) && (
                    <div>
                      <dt className="text-slate-500">
                        Заметка / версия скрипта
                      </dt>
                      <dd className="font-mono text-xs">
                        {detailData.admin_note || detailData.script_version}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-slate-500">Создано / обновлено</dt>
                    <dd className="font-mono text-xs">
                      {detailData.created_at ?? "—"} /{" "}
                      {detailData.updated_at ?? "—"}
                    </dd>
                  </div>
                </dl>
                {detailData.result_data?.domains &&
                  detailData.result_data.domains.length > 0 && (
                    <div className="mt-3">
                      <p className="text-slate-500 mb-1 text-sm">
                        Домены ({detailData.result_data.domains.length})
                      </p>
                      <div className="max-h-[80vh] overflow-auto border border-slate-200 rounded text-xs">
                        <table className="w-full border-collapse min-w-max">
                          <thead className="sticky top-0 bg-slate-100 z-10">
                            <tr>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                Домен
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                Статус
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                URL
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                Протокол
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                DNS ошибка
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left w-[11.2rem] max-w-[11.2rem]">
                                SSL ошибка
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                SSL валид
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left w-[11.2rem] max-w-[11.2rem]">
                                HTTP ошибка
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                HTTP статус
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left w-[11.2rem] max-w-[11.2rem]">
                                HTTP title
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                SSL issuer
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                DNS ms
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                HTTP ms
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                TCP ms
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                Источник
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left w-[16.8rem] max-w-[16.8rem]">
                                IP адреса
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left w-[16.8rem] max-w-[16.8rem]">
                                Редирект
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                final_domain
                              </th>
                              <th className="border-b border-slate-200 px-2 py-1 text-left whitespace-nowrap">
                                Время проверки
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.result_data.domains.map((d, i) => (
                              <tr key={i} className="border-b border-slate-100">
                                <td className="px-2 py-1 font-mono whitespace-nowrap">
                                  {d.name}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.status}
                                </td>
                                <td className="px-2 py-1 font-mono whitespace-nowrap">
                                  {d.http_url ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.protocol ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.dns_error || "—"}
                                </td>
                                <td className="px-2 py-1 w-[11.2rem] max-w-[11.2rem] whitespace-normal break-words">
                                  {d.ssl_error || "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.ssl_valid ?? "—"}
                                </td>
                                <td className="px-2 py-1 w-[11.2rem] max-w-[11.2rem] whitespace-normal break-words">
                                  {d.http_error || "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.http_status ?? "—"}
                                </td>
                                <td className="px-2 py-1 w-[11.2rem] max-w-[11.2rem] whitespace-normal break-words">
                                  {d.http_title || "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.ssl_issuer ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap tabular-nums">
                                  {d.dns_time_ms ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap tabular-nums">
                                  {d.http_time_ms ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap tabular-nums">
                                  {d.tcp_connect_time_ms ?? "—"}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {d.source_file ?? "—"}
                                </td>
                                <td className="px-2 py-1 font-mono w-[16.8rem] max-w-[16.8rem] whitespace-normal break-words">
                                  {d.dns_resolved_ips ?? "—"}
                                </td>
                                <td className="px-2 py-1 font-mono w-[16.8rem] max-w-[16.8rem] whitespace-normal break-words">
                                  {d.http_redirect_url || "—"}
                                </td>
                                <td className="px-2 py-1 font-mono whitespace-nowrap">
                                  {d.final_domain || "—"}
                                </td>
                                <td
                                  className="px-2 py-1 whitespace-nowrap"
                                  title={d.check_timestamp}
                                >
                                  {d.check_timestamp ?? "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
              </>
            ) : (
              <p className="py-8 text-center text-slate-500">
                Ошибка загрузки или данные не найдены
              </p>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default WhiteListCheckerPage;
